import { canonicalizeSequence } from '../format/canonical';
import type { SequenceDocument } from '../format/types';
import { validateSequence, type ValidationError } from '../format/validator';
import { createId } from './defaults';
import type { ProjectState } from './projectTypes';

export interface ImportResult {
  readonly ok: boolean;
  readonly project?: ProjectState;
  readonly errors?: readonly ValidationError[];
}

/**
 * Importiert eine Sequenz als neues Pattern in die Bank. Bei ungueltigem
 * Import bleibt der uebergebene Zustand vollstaendig unveraendert - der
 * Aufrufer darf den zurueckgegebenen Zustand einfach verwerfen.
 */
export function importSequenceJson(state: ProjectState, rawText: string): ImportResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawText);
  } catch (error) {
    return {
      ok: false,
      errors: [{ path: '', code: 'json.parse', message: `Ungueltiges JSON: ${(error as Error).message}` }],
    };
  }
  const result = validateSequence(parsed);
  if (!result.ok) {
    return { ok: false, errors: result.errors };
  }
  const imported = state.patterns.some((pattern) => pattern.id === result.value.id)
    ? {
        ...result.value,
        id: createUniquePatternId(state),
        name: createCopyName(state, result.value.name),
      }
    : result.value;
  return {
    ok: true,
    project: { patterns: [...state.patterns, imported], activePatternId: imported.id },
  };
}

function createUniquePatternId(state: ProjectState): string {
  const usedIds = new Set(state.patterns.map((pattern) => pattern.id));
  let id = createId('seq');
  while (usedIds.has(id)) id = createId('seq');
  return id;
}

function createCopyName(state: ProjectState, sourceName: string): string {
  const usedNames = new Set(state.patterns.map((pattern) => pattern.name));
  let suffix = 1;
  let name = `${sourceName} Kopie`;
  while (usedNames.has(name)) {
    suffix += 1;
    name = `${sourceName} Kopie ${suffix}`;
  }
  return name;
}

export function exportSequenceJson(doc: SequenceDocument): string {
  return canonicalizeSequence(doc);
}
